import {
  DiscussionAndMessages,
  DiscussionContextType,
  DiscussionType,
  Message,
  MessageTypeKey,
} from "@cnx/common";
import { Box, Button, Loader, Textarea, UnstyledButton } from "@mantine/core";
import { useClickOutside, useMergedRef } from "@mantine/hooks";
import { IconSend2 } from "@tabler/icons-react";
import FeatureUploadImages from "components/ImageUploader/FeatureUploadImages";
import { useDocumentsWithId } from "hooks/useDocuments";
import {
  useCreateMessageMutation,
  useUpdateMessageMutation,
} from "hooks/useMessages";
import IconRoundedCamera from "icons/common/rounded-camera.svg";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSocket } from "src/socket/useSocket";
import { useAuthStore } from "stores/authStore";
import { useChatScrollContext } from "../Chat/ChatScrollContext";
import ChatImagePreview from "./ChatImagePreview";

interface ChatInputProps {
  discussion?: DiscussionAndMessages;
  context?: DiscussionContextType;
  contextId?: string;
  editingMessage?: Message | null;
  setFirstUnreadMessageId: (id: string | null) => void;
  onDiscussionCreated?: (discussionId: string) => void;
  onCancelEdit?: () => void;
}

const ChatInput = forwardRef<HTMLDivElement, ChatInputProps>(
  (
    {
      discussion,
      context,
      contextId,
      editingMessage,
      setFirstUnreadMessageId,
      onDiscussionCreated,
      onCancelEdit,
    },
    ref
  ) => {
    const socket = useSocket();
    const { t } = useTranslation("discussions");
    const [message, setMessage] = useState("");
    const [opened, setOpened] = useState(false);
    const currentUserId = useAuthStore(state => state.currentUserId);
    const fullName = useAuthStore(state => state.fullName);
    const { mutateAsync, isPending: mutationIsLoading } =
      useCreateMessageMutation();
    const { mutateAsync: mutateUpdateAsync, isPending: updateIsLoading } =
      useUpdateMessageMutation();
    const textInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(-1);
    const { documents, append, removeById, getBlobs, clear } =
      useDocumentsWithId();
    const { setShouldScroll } = useChatScrollContext();

    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (editingMessage) {
        setMessage(editingMessage.message ?? "");
        textareaRef.current?.focus();
      } else {
        setMessage("");
      }
    }, [editingMessage]);

    const closePanel = () => {
      if (!opened) return;
      if (selectedImageIndex !== -1) return;
      setOpened(false);
      clear();
    };

    const clickOutsideRef = useClickOutside(closePanel);
    const wrapperRef = useMergedRef(clickOutsideRef, ref);

    const showTyping = (typing: boolean) => {
      if (!socket || !discussion?.id || !currentUserId || !!editingMessage)
        return;
      socket.emit("typing", {
        discussionId: discussion.id,
        typing,
        userId: currentUserId,
        userName: fullName ?? "",
      });
    };

    const isEditingWithAttachments = !!editingMessage?.attachments?.length;

    const sendMessage = async () => {
      showTyping(false);
      if (mutationIsLoading || updateIsLoading) return;
      if (!editingMessage && message === "" && documents.length === 0) return;
      if (editingMessage && message === (editingMessage.message ?? "")) return;

      setShouldScroll(true);

      if (editingMessage?.id && discussion) {
        await mutateUpdateAsync({
          body: { message },
          id: editingMessage.id,
          discussionId: discussion.id,
        });
        onCancelEdit?.();
        setMessage("");
        setOpened(false);
        setSelectedImageIndex(-1);
        clear();
        return;
      }

      if (discussion) {
        await mutateAsync({
          body: {
            discussionId: discussion.id,
            messageTypeKey: MessageTypeKey.STANDARD,
            pinned: 0,
            message,
          },
          attachments: getBlobs(),
          socketPayload: {
            discussionType: discussion.discussionType,
            context: discussion.context,
            message,
          },
        });
      }

      if (!discussion && context && contextId) {
        const result = await mutateAsync({
          body: {
            context,
            contextId,
            messageTypeKey: MessageTypeKey.STANDARD,
            pinned: 0,
            message,
          },
          attachments: getBlobs(),
          socketPayload: {
            discussionType: DiscussionType.CONTEXT,
            context,
            message,
          },
        });
        const newDiscussionId = result?.data?.discussionId;

        if (newDiscussionId) {
          onDiscussionCreated?.(newDiscussionId);
        }
      }

      setOpened(false);
      setSelectedImageIndex(-1);
      clear();
      setMessage("");
      setFirstUnreadMessageId(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        sendMessage();
      }
    };

    const processMessageChange = (value: string) => {
      setMessage(value);
      if (value !== "") showTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (value === "") showTyping(false);
      }, 3000);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      processMessageChange(e.target.value);
    };

    const handleTextareaChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      processMessageChange(e.target.value);
    };

    const handleTextareaKeyDown = (
      e: React.KeyboardEvent<HTMLTextAreaElement>
    ) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    };

    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }

      return () => {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
      };
    }, []);

    return (
      <Box ref={wrapperRef} className="bg-white">
        {opened && !editingMessage && (
          <Box
            style={{
              position: "absolute",
              bottom: "100%",
              left: 0,
              right: 0,
              backgroundColor: "white",
              minHeight: "125px",
              borderRadius: "15px 15px 0 0",
              border: "1px solid var(--mantine-color-gray-3)",
              borderBottom: "none",
              overflow: "hidden",
              zIndex: 100,
            }}
          >
            <ChatImagePreview
              sendMessage={sendMessage}
              mutationIsLoading={mutationIsLoading}
              isSending={mutationIsLoading}
              handleChange={handleChange}
              handleKeyDown={handleKeyDown}
              textInputRef={textInputRef as React.RefObject<HTMLInputElement>}
              message={message}
              selectedImageIndex={selectedImageIndex}
              setSelectedImageIndex={setSelectedImageIndex}
              documents={documents}
              removeById={removeById}
              onAddMore={append}
              imageLimit={5}
            />

            <FeatureUploadImages
              setSelectedImageIndex={setSelectedImageIndex}
              chatFeature={true}
              imageLimit={5}
              previewImagesPerRow={3}
              documents={documents}
              onImageUpload={append}
              onRemoveImage={removeById}
            />
          </Box>
        )}

        <Box className="pl-5 pr-5 min-h-[66px] flex items-end py-[17px] gap-4">
          {/* <AttachmentPopover /> */}

          {!editingMessage && (
            <Button p={0} bg="white" onClick={() => setOpened(o => !o)}>
              <IconRoundedCamera className="text-gray-medium w-6 h-6" />
            </Button>
          )}

          <Textarea
            ref={textareaRef}
            placeholder={t("message")}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleTextareaKeyDown}
            className="grow"
            maxLength={4096}
            autosize
            minRows={1}
            maxRows={5}
          />

          {(message !== "" ||
            documents.length > 0 ||
            isEditingWithAttachments) &&
          (!editingMessage || message !== (editingMessage.message ?? "")) ? (
            <UnstyledButton
              onClick={sendMessage}
              disabled={mutationIsLoading || updateIsLoading}
              className="text-white w-8 h-8 flex items-center justify-center rounded-full bg-blue-dark"
            >
              {mutationIsLoading || updateIsLoading ? (
                <Loader size={16} color="white" />
              ) : (
                <IconSend2 width={18} height={18} />
              )}
            </UnstyledButton>
          ) : (
            <UnstyledButton
              onClick={sendMessage}
              disabled={true}
              className="text-white w-8 h-8 flex items-center justify-center rounded-full bg-gray-medium"
            >
              <IconSend2 width={18} height={18} />
            </UnstyledButton>
          )}
        </Box>
      </Box>
    );
  }
);

export default ChatInput;
